/**
 * モーショングラフィックス (プロンプト入力モードの 3 タブ目) の生成リクエスト
 * (docs/features/motion-graphics-mode-plan.md §5.4・§6.1)。
 *
 * - checkMotionLayeredReadiness: 生成ボタンを押せるか (設定欄の無効化と理由・ハンドラの前提の検証で共用)
 * - buildMotionLayeredRequest: `POST /api/jobs/motion-layered/generate` の body
 * - motionLayeredRequestErrorMessage: 受付エラー (error_code / HTTP ステータス) の利用者向け文言
 */
import type { GenerationAsset } from '../../components/generation/state/types';
import { friendlyJobErrorMessage } from '../../components/generation/scenario/utils';
import { resolveErrorMessage } from '../../utils/getErrorMessage';
import { MOTION_LAYERED_UNAVAILABLE_MESSAGES, motionLayeredUnavailableMessage, type MotionLayeredAvailability } from './availability';
import {
  MOTION_LAYERED_LAYER_REQUIRED_NOTICE,
  getMotionLayeredAssetNotice,
  isMotionLayerCandidate,
  type MotionLayeredEndState,
  type MotionLayeredSettings,
  type MotionLayeredSoundEffects,
} from './layeredPromptMode';
import { isCheckedLayerStack, parseLayerStack, type LayerStack } from './layerStack';
import type { MotionModel } from './model';
import type { MotionQuality } from './quality';

/** backend の INSTRUCTION_MAX_CHARS / LAYER_NAME_MAX_CHARS (backend/services/motion_layered/prompts_layered.py) の写し */
export const MOTION_LAYERED_PROMPT_MAX_CHARS = 2000;
export const MOTION_LAYERED_LAYER_NAME_MAX_CHARS = 80;

export const MOTION_LAYERED_PROMPT_REQUIRED_MESSAGE = '動きの指示を入力してください';
export const MOTION_LAYERED_PROMPT_TOO_LONG_MESSAGE = `動きの指示は ${MOTION_LAYERED_PROMPT_MAX_CHARS} 文字以内で入力してください`;
export const MOTION_LAYERED_CREDITS_INSUFFICIENT_MESSAGE = 'ポイントが不足しています';
export const MOTION_LAYERED_STORAGE_EXCEEDED_MESSAGE = 'ストレージ容量の上限に達しました';

export type MotionLayeredTarget = { asset: GenerationAsset; stack: LayerStack };

/**
 * 生成できる状態。target が無く pendingAsset がある = SVG 原本でレイヤー構成が未解析。
 * ハンドラは `ensureMaterialLayers` で解析してから生成する (layeredPromptMode.isMotionLayerCandidate)。
 */
export type MotionLayeredReadiness =
  | { ok: true; target: MotionLayeredTarget | null; pendingAsset: GenerationAsset | null; credits: number }
  | { ok: false; reason: 'unavailable' | 'asset' | 'prompt' | 'storage' | 'credits'; message: string };

/**
 * 選択済み素材のうち生成に使うレイヤー素材 (入稿ゲートを通った最初の 1 件)。
 * 判定は isLayerStackMaterial と同じ (`layerStack` または `generation_metadata.layer_stack`)。
 */
export function findMotionLayeredTarget(assets: ReadonlyArray<GenerationAsset>): MotionLayeredTarget | null {
  for (const asset of assets) {
    const gm = (asset as { generation_metadata?: Record<string, unknown> | null }).generation_metadata;
    for (const raw of [asset.layerStack, gm?.layer_stack]) {
      const stack = parseLayerStack(raw);
      if (stack && isCheckedLayerStack(stack)) return { asset, stack };
    }
  }
  return null;
}

/**
 * 生成ボタンを押せるか。順に: 使えるか (availability・fail-closed) → 素材 (レイヤー素材 1 件だけ) →
 * 動きの指示 (1〜2000 字) → ストレージ → 残高 (availability の定額以上)。
 * balance が不明 (残高の取得前) のときは残高では止めない (backend が 402 で止める・既存の動画生成と同じ)。
 */
export function checkMotionLayeredReadiness({
  availability,
  assets,
  prompt,
  balance,
  isStorageExceeded,
}: {
  availability: MotionLayeredAvailability | null | undefined;
  assets: ReadonlyArray<GenerationAsset>;
  prompt: string;
  balance: number | null | undefined;
  isStorageExceeded: boolean;
}): MotionLayeredReadiness {
  if (!availability || !availability.enabled) {
    return { ok: false, reason: 'unavailable', message: motionLayeredUnavailableMessage(availability) ?? MOTION_LAYERED_UNAVAILABLE_MESSAGES.fetch_failed };
  }
  const assetNotice = getMotionLayeredAssetNotice(assets);
  const target = findMotionLayeredTarget(assets);
  const pendingAsset = target ? null : (assets.find((a) => isMotionLayerCandidate(a)) ?? null);
  if (assetNotice || (!target && !pendingAsset)) {
    return { ok: false, reason: 'asset', message: assetNotice ?? MOTION_LAYERED_LAYER_REQUIRED_NOTICE };
  }
  const trimmed = prompt.trim();
  if (!trimmed) return { ok: false, reason: 'prompt', message: MOTION_LAYERED_PROMPT_REQUIRED_MESSAGE };
  if (trimmed.length > MOTION_LAYERED_PROMPT_MAX_CHARS) {
    return { ok: false, reason: 'prompt', message: MOTION_LAYERED_PROMPT_TOO_LONG_MESSAGE };
  }
  if (isStorageExceeded) return { ok: false, reason: 'storage', message: MOTION_LAYERED_STORAGE_EXCEEDED_MESSAGE };
  if (typeof balance === 'number' && Number.isFinite(balance) && availability.credits > 0 && balance < availability.credits) {
    return { ok: false, reason: 'credits', message: MOTION_LAYERED_CREDITS_INSUFFICIENT_MESSAGE };
  }
  return { ok: true, target, pendingAsset, credits: availability.credits };
}

export type MotionLayeredGenerateRequest = {
  material_id: string;
  prompt: string;
  duration_seconds: number;
  aspect_ratio: string;
  sound_effects: MotionLayeredSoundEffects;
  end_state: MotionLayeredEndState;
  locked_layer_indexes: number[];
  /** index → 表示名の上書き。元の名前と同じ・空の名前は送らない (上書きが 1 件も無ければキーごと省く) */
  layer_names?: Record<string, string>;
  quality: MotionQuality;
  /** 初稿の AI モデル (features/motion/model) */
  model: MotionModel;
};

/**
 * body を組み立てる。レイヤーの名前と固定は layerEditsStore の値 (素材 id ごと・layer.index がキー)。
 * この素材に無い index は落とす (backend は範囲外を 400 にする)。
 */
export function buildMotionLayeredRequest({
  target,
  prompt,
  settings,
  names,
  locked,
  quality,
  model,
}: {
  target: MotionLayeredTarget;
  prompt: string;
  settings: MotionLayeredSettings;
  names: Record<number, string> | undefined;
  locked: ReadonlyArray<number> | undefined;
  quality: MotionQuality;
  model: MotionModel;
}): MotionLayeredGenerateRequest {
  const layersByIndex = new Map(target.stack.layers.map((layer) => [layer.index, layer]));
  const layerNames: Record<string, string> = {};
  for (const [key, value] of Object.entries(names ?? {})) {
    const layer = layersByIndex.get(Number(key));
    if (!layer || typeof value !== 'string') continue;
    const name = value.trim().slice(0, MOTION_LAYERED_LAYER_NAME_MAX_CHARS);
    if (!name || name === layer.name) continue;
    layerNames[String(layer.index)] = name;
  }
  const lockedIndexes = [...new Set((locked ?? []).filter((i) => layersByIndex.has(i)))].sort((a, b) => a - b);
  return {
    material_id: target.asset.id,
    prompt: prompt.trim(),
    duration_seconds: settings.durationSeconds,
    aspect_ratio: settings.aspectRatio,
    sound_effects: settings.soundEffects,
    end_state: settings.endState,
    locked_layer_indexes: lockedIndexes,
    ...(Object.keys(layerNames).length > 0 ? { layer_names: layerNames } : {}),
    // 品質モード (ライト / スタンダード / ダメ出し・2026-10-02 からレイヤー素材もライト可。忠実度検査は light の試写でも省かれない)
    quality,
    model,
  };
}

/** 受付エラーの扱い。purchase = 購入モーダルを開く / refreshAvailability = availability を取り直す (料金行・暫定ゲートが変わった) */
export type MotionLayeredRequestError = {
  message: string;
  purchase: boolean;
  refreshAvailability: boolean;
};

/**
 * `POST /api/jobs/motion-layered/generate` の受付エラーを利用者向けにする (backend/routes/async_jobs/motion_layered.py)。
 * 402 残高不足 / 403 MOTION_LAYERED_NOT_AVAILABLE / 400 NOT_LAYER_MATERIAL・LAYER_MATERIAL_INVALID・本文 /
 * 404 素材なし / 500 LAYER_SNAPSHOT_FAILED / 503 MOTION_RENDERER_UNAVAILABLE (レンダラー)・料金行なし。
 */
export function motionLayeredRequestError(error: unknown): MotionLayeredRequestError {
  const resp = (error as { response?: { status?: number; data?: { error?: string; error_code?: string } } } | null)?.response;
  const status = resp?.status;
  const code = resp?.data?.error_code;
  const serverMessage = typeof resp?.data?.error === 'string' && resp.data.error.trim() ? resp.data.error : null;
  const result = (message: string, extra: Partial<MotionLayeredRequestError> = {}): MotionLayeredRequestError => ({
    message, purchase: false, refreshAvailability: false, ...extra,
  });
  if (status === 402 || code === 'INSUFFICIENT_CREDITS') {
    return result(friendlyJobErrorMessage(error) ?? 'ポイントが不足しています。ポイントを購入してから再度お試しください。', { purchase: true });
  }
  switch (code) {
    case 'MOTION_LAYERED_NOT_AVAILABLE':
      return result(MOTION_LAYERED_UNAVAILABLE_MESSAGES.restricted, { refreshAvailability: true });
    case 'NOT_LAYER_MATERIAL':
      return result(MOTION_LAYERED_LAYER_REQUIRED_NOTICE);
    case 'LAYER_MATERIAL_INVALID':
      return result(serverMessage ?? 'このレイヤー素材は使えません。SVG をアップロードし直してください');
    case 'LAYER_SNAPSHOT_FAILED':
      return result('レイヤー素材の準備に失敗しました。しばらくしてからもう一度お試しください');
    case 'MOTION_RENDERER_UNAVAILABLE':
      // 一時的 (レンダラーの起動待ち・障害)。availability には含めないので取り直さない
      return result(serverMessage ?? '生成サーバーに接続できませんでした。しばらくしてからもう一度お試しください');
    default:
      break;
  }
  if (status === 503) {
    return result(`モーショングラフィックスの${MOTION_LAYERED_UNAVAILABLE_MESSAGES.rate_not_configured}`, { refreshAvailability: true });
  }
  if (status === 404) return result('素材が見つかりません。素材を選び直してください');
  if (status === 400 && serverMessage) return result(serverMessage);
  const friendly = friendlyJobErrorMessage(error);
  if (friendly) return result(friendly);
  return result(`モーショングラフィックスのリクエストに失敗しました: ${resolveErrorMessage(error)}`);
}
